import React from 'react';
import Icon from './Icon';

const DEFAULT_SIZES = [10, 25, 50, 100, 250, 500];

/**
 * <Pagination
 *    page={page}
 *    pages={pages}
 *    total={total}
 *    limit={limit}
 *    onPage={setPage}
 *    onLimit={setLimit}
 *    sizes={[10, 25, 50]}
 * />
 */
export default function Pagination({
  page,
  pages,
  total,
  limit,
  onPage,
  onLimit,
  sizes = DEFAULT_SIZES,
  style,
  ...rest
}) {
  const from = total === 0 ? 0 : page * limit + 1;
  const to   = Math.min(page * limit + limit, total);
  const disabledPrev = page === 0;
  const disabledNext = page >= (pages - 1);

  const btn = (disabled, onClick, icon) => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28,
        padding: 0,
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = 'var(--accent)'; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <Icon name={icon} size="xs" strokeWidth={2.2}/>
    </button>
  );

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px var(--sp-5)',
        background: 'var(--bg-surface)',
        borderTop: '1px solid var(--border-subtle)',
        gap: 'var(--sp-3)',
        flexWrap: 'wrap',
        ...style,
      }}
      {...rest}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
          fontFamily: 'var(--font-mono)', letterSpacing: 'var(--ls-wide)',
          textTransform: 'uppercase', fontWeight: 'var(--fw-bold)',
        }}>
          Rows
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {sizes.map(sz => {
            const active = limit === sz;
            return (
              <button
                key={sz}
                type="button"
                onClick={() => onLimit(sz)}
                style={{
                  padding: '3px 8px',
                  background: active ? 'var(--accent-dim)' : 'transparent',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-xs)',
                  color: active ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: 'var(--fs-2xs)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: active ? 'var(--fw-bold)' : 'var(--fw-med)',
                  cursor: 'pointer',
                  transition: 'all var(--dur-fast) var(--ease-out)',
                }}
              >
                {sz}
              </button>
            );
          })}
        </div>
        {total > 0 && (
          <span style={{
            fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
            fontFamily: 'var(--font-mono)',
          }}>
            {from.toLocaleString('tr-TR')}–{to.toLocaleString('tr-TR')} / {total.toLocaleString('tr-TR')}
          </span>
        )}
      </div>

      {total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {btn(disabledPrev, () => onPage(0),          'pageFirst')}
          {btn(disabledPrev, () => onPage(page - 1),   'chevronLeft')}
          <span style={{
            fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)',
            padding: '0 10px', minWidth: 70, textAlign: 'center',
            fontFamily: 'var(--font-mono)',
          }}>
            {page + 1} / {pages || 1}
          </span>
          {btn(disabledNext, () => onPage(page + 1),   'chevronRight')}
          {btn(disabledNext, () => onPage(pages - 1),  'pageLast')}
        </div>
      )}
    </div>
  );
}
